import * as React from "react";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import styled, { css } from "styled-components";
import useResizeObserver from "@react-hook/resize-observer";
import { ButtonToolbar } from "components/bootstrap";
import {
  TABLE_ROW_HOVER_TRANSITION,
  TABLE_ROW_PINNED_HOVER_BG_VAR,
  flattenTableBackground,
  isStripedRowIndex
} from "components/bootstrap/Table";
import {
  ACTIONS_COL_ID,
  CELL_PADDING_HORIZONTAL,
  CELL_PADDING_VERTICAL
} from "components/common/EntityDataTable/Constants";
import { actionsHeaderWidthVar } from "components/common/EntityDataTable/CSSVariables";
const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 100%;
`;
const BackgroundFoundation = styled.div`
  height: 100%;
  width: var(${actionsHeaderWidthVar});
  overflow: hidden;
`;
const HeaderBackground = styled(BackgroundFoundation)(
  ({ theme }) => css`
    background-color: ${flattenTableBackground(theme, theme.colors.table.head.background)};
  `
);
const Actions = styled.div(
  ({ $isStripedRow, theme }) => css`
    display: flex;
    justify-content: flex-end;
    padding: ${CELL_PADDING_VERTICAL}px ${CELL_PADDING_HORIZONTAL}px;
    background-color: ${flattenTableBackground(
    theme,
    $isStripedRow ? theme.colors.table.row.backgroundStriped : theme.colors.table.row.background
  )};
    height: 100%;
    align-items: center;
    transition: ${TABLE_ROW_HOVER_TRANSITION};

    tr:hover & {
      background-color: var(${TABLE_ROW_PINNED_HOVER_BG_VAR});
    }
  `
);
const ActionCell = ({
  row,
  entityActions,
  onWidthChange
}) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) {
      onWidthChange(row.id, ref.current.getBoundingClientRect().width);
    }
  }, [row.id, onWidthChange]);
  useResizeObserver(ref, ({ contentRect: { width } }) => onWidthChange(row.id, width));
  return /* @__PURE__ */ React.createElement(AlignRight, null, /* @__PURE__ */ React.createElement(BackgroundFoundation, null, /* @__PURE__ */ React.createElement(Actions, { $isStripedRow: isStripedRowIndex(row.index + 1) }, /* @__PURE__ */ React.createElement(ButtonToolbar, { ref }, entityActions(row.original)))));
};
const useActionsColumnDefinition = ({
  colWidth,
  entityActions,
  hasRowActions,
  onWidthChange
}) => {
  const columnHelper = createColumnHelper();
  const cell = useCallback(
    ({ row }) => entityActions ? /* @__PURE__ */ React.createElement(ActionCell, { row, entityActions, onWidthChange }) : null,
    [entityActions, onWidthChange]
  );
  const header = useCallback(
    () => /* @__PURE__ */ React.createElement(AlignRight, null, /* @__PURE__ */ React.createElement(HeaderBackground, null)),
    []
  );
  return useMemo(
    () => columnHelper.display({
      id: ACTIONS_COL_ID,
      size: colWidth,
      enableHiding: false,
      enablePinning: true,
      enableResizing: false,
      // The actions/tail column is always present. We only hide its content when there are no row actions.
      header: hasRowActions ? header : void 0,
      cell: hasRowActions ? cell : void 0,
      meta: {
        hideCellPadding: true
      }
    }),
    [colWidth, cell, columnHelper, hasRowActions, header]
  );
};
export default useActionsColumnDefinition;
